import React from 'react';

import { MenuItem, Menu } from '@material-ui/core';
import ArchiveIcon from '@material-ui/icons/Archive';
import LinkIcon from '@material-ui/icons/Link';
import UnarchiveIcon from '@material-ui/icons/Unarchive';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { getQuestionDetailsRoute } from 'constants/helper';
import { DotsIcon, DuplicateIcon } from 'constants/icons';
import theme from 'constants/theme';

import { showQuestionDetails } from 'services/questionDetails';
import { saveQuestion } from 'services/reviews';
import { showSuccessSnackbar, showErrorSnackbar } from 'services/snackbar';

import CircularOutlineIcon from 'uiToolkit/CircularOutlineIcon';
import { Typography13 } from 'uiToolkit/Typography';

const QuestionMenuOptions = ({ question, menuOptions = [], updateData, source }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const dispatch = useDispatch();
  const history = useHistory();

  const handleClick = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (e, option) => {
    e.stopPropagation();
    setAnchorEl(null);
    switch (option) {
      case 'DUPLICATE':
        dispatch(showQuestionDetails({ questionId: question.id, action: 'copy', source, history }));
        break;
      case 'ARCHIVE':
        update(true);
        break;
      case 'UNARCHIVE':
        update(false);
        break;
      default:
        break;
    }
  };

  const update = (isArchived) => {
    const op = isArchived ? 'archive' : 'unarchived';
    saveQuestion({ question: { ...question, isArchived }, source, buttonType: op })
      .then((res) => {
        if (res) {
          updateData();
        } else {
          dispatch(showErrorSnackbar(res.message || `Sorry, unable to ${op} the question!`));
        }
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message || `Sorry, unable to ${op} the question!`));
      });
  };

  const generateQuestionLink = () => {
    const host = `${window.location.protocol}//${window.location.host}`;
    const path = getQuestionDetailsRoute({
      location: history.location,
      questionId: question.id,
      action: 'edit',
      source,
    });
    return host + path;
  };

  const copy = () => dispatch(showSuccessSnackbar('Link is copied to clipboard'));

  const isApplicable = (option) => menuOptions.includes(option);

  return menuOptions.length > 0 ? (
    <>
      <CircularOutlineIcon onClick={handleClick} color="primary" icon={StyledDotsIcon} />
      <StyledMenu
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        keepMounted
        open={open}
        onClose={handleClose}
        disablePortal
      >
        {isApplicable('DUPLICATE') && (
          <StyledMenuItem onClick={(e) => handleClose(e, 'DUPLICATE')}>
            <DuplicateIcon leftGutter={0.25} size="fixed12px" color={theme.palette.icons.blackv1} />
            <Typography13 color={theme.palette.text.blackv1} leftGutter={2.25}>
              Duplicate
            </Typography13>
          </StyledMenuItem>
        )}
        {isApplicable('COPY_LINK') && (
          <CopyToClipboard text={generateQuestionLink()} onCopy={copy}>
            <StyledMenuItem $materialIcon onClick={(e) => handleClose(e, 'COPY_LINK')}>
              <StyledLinkIcon />
              <Typography13 color={theme.palette.text.blackv1} leftGutter={2}>
                Copy Link
              </Typography13>
            </StyledMenuItem>
          </CopyToClipboard>
        )}
        {isApplicable('ARCHIVE') && (
          <StyledMenuItem $materialIcon onClick={(e) => handleClose(e, 'ARCHIVE')}>
            <ArchiveIcon />
            <Typography13 color={theme.palette.text.blackv1} leftGutter={2}>
              Archive
            </Typography13>
          </StyledMenuItem>
        )}
        {isApplicable('UNARCHIVE') && (
          <StyledMenuItem $materialIcon onClick={(e) => handleClose(e, 'UNARCHIVE')}>
            <UnarchiveIcon />
            <Typography13 color={theme.palette.text.blackv1} leftGutter={2}>
              Unarchive
            </Typography13>
          </StyledMenuItem>
        )}
      </StyledMenu>
    </>
  ) : (
    <></>
  );
};

const StyledDotsIcon = styled(DotsIcon)`
  width: 13px;
  margin-top: 1px;
`;

const StyledMenu = styled(Menu)`
  ul {
    padding-top: 10px;
    padding-bottom: 10px;
  }
  margin-top: 0.5rem;
`;

const StyledMenuItem = styled(MenuItem)`
  padding-right: 30px;
  ${(props) =>
    props.$materialIcon &&
    css`
      svg {
        font-size: 1rem;
        width: 16px;
        height: 16px;
        color: ${(props) => props.theme.palette.icons.black.v1};
      }
    `}
`;

const StyledLinkIcon = styled(LinkIcon)`
  transform: rotate(45deg);
`;

export default QuestionMenuOptions;
